<script lang="ts">
  /**
   * The Exposed Sensitive Documents page body.
   *
   * It owns the derivation chain and nothing else; every decision in it comes from the
   * firebase-free view model (`../exposedSensitiveDocuments` and its `./activeInfoTypes` half),
   * and every piece of rendering from a leaf component in this directory. The chain, in order (spec 6.2, build default R11):
   *
   *   loaded rows
   *     -> drop rows from a retired provider   (a retired provider's rows were swept; if any
   *                                             survived a failed sweep they are not shown)
   *     -> provider filter
   *     -> trim to the active info-type list   -> the per-status counts on the status select are
   *                                                taken here, so they sum to the cards' union
   *     -> status filter                       -> the counter-card counts are taken here, so a
   *                                                card counts only what the provider, info-type
   *                                                and status filters admit (build default R11,
   *                                                spec 9.5), and a both-document counts under
   *                                                all three cards. A failed status read makes
   *                                                this step pass every row (spec 9.3)
   *     -> exposure filter                     -> sort (client-side, over the whole set, on the
   *                                                *trimmed* Findings total the row displays)
   *     -> page of 25
   *
   * The loaded rows are the rows *so far* (#2265): the page renders after the first batch and the
   * chain re-runs as each batch lands, so sort and every filter work over a partial load. Until
   * the last batch the counts are labelled provisional and a progress bar sits under the cards.
   *
   * The selected row - the one whose detail panel is open beside the table (6.4) - is held here
   * as an id and resolved against the page on screen, so the panel can only ever describe a row
   * the reviewer can still see. It is deliberately not in the URL: the spec does not ask for a
   * deep link to a row, and adding one belongs in the filter codec, not in a second mechanism.
   *
   * The category search (#2247) is the fourth thing here: a combobox on the filter bar's second
   * line whose every tick writes the same `?infoTypes` this page already reads - no draft, no
   * Apply. Its rows (exposure + provider view) are also the pool the line-3 category chips count
   * "k of n types" against. The filter bar's write-only button saves the *org's* default through
   * the settings route instead - over the **active** list (spec 483) - which is the one thing on this page a reader without
   * `org:write` may not do.
   *
   * All three filters live in the URL so a view is shareable; they are read through
   * `querystring` and written with `push`, the way every other filter in this dashboard is. Both
   * the reading and the writing are view-model functions, so what a given URL means - and what a
   * given set of filters encodes to - is probed rather than inspected here. `?infoTypes` has
   * three states, not two: absent = follow the org's saved default, present and empty = show all,
   * non-empty = this reader's own list (spec 6.1).
   */
  import { location, push, querystring } from "svelte-spa-router";

  import { PageLoadingSpinner } from "$lib/ui/loading-spinner";
  import {
    orgExposedSensitiveDocumentsStatusStore,
    orgExposedSensitiveDocumentsMetadataStore,
    orgExposedSensitiveDocumentsWithStatusStore,
  } from "$lib/stores/orgExposedSensitiveDocuments";
  import { selectedOrgIdStore, selectedOrgStore } from "$lib/stores/orgs";
  import { authStateStore } from "$lib/stores/user";
  import { listCustomInfoTypes } from "$lib/services/customInfoTypes";
  import { fetchDefaultInfoTypes, saveDefaultInfoTypes } from "$lib/services/exposedSensitiveDocumentsSettings";
  import { saveExposedDocumentStatus } from "$lib/services/exposedSensitiveDocumentStatus";
  import { formatNumberWithCommas } from "$lib/utils/number";
  import { isRedactiveUser } from "$lib/utils/helpers";
  import { hasOrgClaim } from "$lib/utils/orgClaims";
  import { toaster } from "$lib/utils/toaster";
  import type { NamedCustomInfoType } from "../categoryLabels";
  import {
    countByExposureKind,
    decodeExposureFilters,
    DEFAULT_PAGE_SIZE,
    DEFAULT_SORT,
    dropRetiredRows,
    effectiveSort,
    emptyStateVariant,
    encodeExposureFilters,
    FOLLOW_ORG_DEFAULT,
    metadataForProvider,
    NO_EXPOSURE_FILTERS,
    paginate,
    PROVIDERS,
    providersWithStatus,
    publishedAtOf,
    rowMatchesExposure,
    rowMatchesProvider,
    SHOW_ALL_INFO_TYPES,
    skippedMetadataOnScreen,
    sortTrimmedRows,
    type ExposureFilters,
    type RowSort,
    type SortColumn,
    type SortDirection,
  } from "../exposedSensitiveDocuments";
  import CategorySearchCombobox from "./CategorySearchCombobox.svelte";
  import ExposedDocumentDetailPanel from "./ExposedDocumentDetailPanel.svelte";
  import ExposedDocumentsEmptyState from "./ExposedDocumentsEmptyState.svelte";
  import ExposedDocumentsTable from "./ExposedDocumentsTable.svelte";
  import ExposureCounterCard from "./ExposureCounterCard.svelte";
  import ExposureProviderSelect from "./ExposureProviderSelect.svelte";
  import ExposureStatusSelect from "./ExposureStatusSelect.svelte";
  import ExposureTruncationNotice from "./ExposureTruncationNotice.svelte";
  import ActiveCategoryChips from "./ActiveCategoryChips.svelte";
  import CategoryFilterBar from "./CategoryFilterBar.svelte";
  import SkippedProviderWarning from "./SkippedProviderWarning.svelte";
  import {
    activeCategoryChips,
    afterOrgDefaultSaved,
    infoTypeFilterControls,
    removeInfoTypes,
    resolveActiveInfoTypes,
    trimRowsToInfoTypes,
  } from "./activeInfoTypes";
  import { EXPOSURE_FILTERS } from "./exposureTones";
  import { categoryMembershipOf, expandForSave } from "./categorySearch";
  import { countByStatus, filterRowsByStatus } from "./status";
  import type { SaveStatus } from "./statusSave";

  // --- URL state ------------------------------------------------------------------------------

  const filters = $derived(decodeExposureFilters($querystring));
  const exposureFilter = $derived(filters.exposure);
  const providerFilter = $derived(filters.provider);

  /**
   * Write the filters into the URL; a `null` drops its parameter, and an `org-default`
   * info-type selection drops `?infoTypes` entirely. `push` keeps this a hash-fragment change,
   * so `?exposure=` and `?provider=` survive alongside it with no reload.
   */
  const setFilters = (next: Partial<ExposureFilters>) => {
    push(`${$location}${encodeExposureFilters({ ...filters, ...next })}`);
  };

  // --- The org's saved default info types -----------------------------------------------------

  /**
   * `undefined` until the settings GET lands, then `null` if it failed or was forbidden and the
   * saved list otherwise. The two are deliberately different values: the rows show everything in
   * both cases rather than an error, because nothing is being hidden and the reader could do
   * nothing about it (spec R4), but only the settled `null` lets the filter bar say so - see
   * `resolveActiveInfoTypes`. One fetch per org selection, in an `$effect` and nowhere else: an
   * `onMount` as well is the double-fetch bug `SensitiveDataTree` has.
   */
  let orgDefaultInfoTypes = $state<string[] | null | undefined>(undefined);

  $effect(() => {
    const orgId = $selectedOrgIdStore;
    // With no org there is nothing to fetch, so the bar is settled on "nothing saved" at once.
    orgDefaultInfoTypes = orgId ? undefined : null;
    if (!orgId) return;
    let stale = false;
    void fetchDefaultInfoTypes(orgId).then((infoTypes) => {
      if (!stale) orgDefaultInfoTypes = infoTypes;
    });
    return () => {
      stale = true;
    };
  });

  // --- The org's enabled custom info types ---------------------------------------------------

  /**
   * The org's *enabled* custom info types, so a custom name chips as its own category, labelled
   * as on the Sensitive Data Detection page, instead of falling through to Uncategorised (spec
   * section 5, 6.3). Same lifecycle as the settings GET above: one read per org selection, in an
   * `$effect`, with a `stale` guard so a superseded org's answer is dropped. A failure is an
   * empty list and nothing on screen - a custom name then reads as Uncategorised, which is the
   * page's honest fall-through, and the reader could do nothing about the failure anyway (spec
   * R4).
   */
  let enabledCustomInfoTypes = $state<NamedCustomInfoType[]>([]);

  $effect(() => {
    const orgId = $selectedOrgIdStore;
    enabledCustomInfoTypes = [];
    if (!orgId) return;
    let stale = false;
    void listCustomInfoTypes(orgId)
      .then((types) => {
        if (!stale) {
          enabledCustomInfoTypes = types
            .filter((type) => type.enabled)
            .map((type) => ({ name: type.name, displayName: type.displayName }));
        }
      })
      .catch(() => {
        // A failed or forbidden read is an empty list, never an error on screen.
      });
    return () => {
      stale = true;
    };
  });

  // --- Local view state -----------------------------------------------------------------------

  let sort = $state<RowSort>({ ...DEFAULT_SORT });
  let currentPage = $state(1);
  /** The row whose detail panel is open (spec 6.4). An id, not a row, so a re-query cannot pin a stale copy. */
  let selectedRowId = $state<string | null>(null);

  /**
   * The org the page is looking at, or null before one is selected. Null rather than `""`: an
   * empty id would read `organisations//...`, so the readers and the detail panel wait for a
   * real one.
   */
  const orgId = $derived<string | null>($selectedOrgIdStore ?? null);

  // A filter, sort or org change makes the old page number meaningless, so go back to the first
  // page - and the open panel with it, because the row it describes is no longer where the
  // reviewer left it. Writing `currentPage` and `selectedRowId` without reading them keeps this
  // effect from re-triggering itself.
  $effect(() => {
    void orgId;
    void exposureFilter;
    void providerFilter;
    void filters.infoTypes;
    void filters.statuses;
    void sort.column;
    void sort.direction;
    currentPage = 1;
    selectedRowId = null;
  });

  // --- The derivation chain -------------------------------------------------------------------

  const documents = $derived($orgExposedSensitiveDocumentsWithStatusStore);
  const metadataState = $derived($orgExposedSensitiveDocumentsMetadataStore);

  // The rows arrive in batches (#2265). The page renders from the first batch on, over the rows
  // loaded so far; only the metadata and that first batch hold the spinner. The status read never
  // joins it: until it lands every row shows Open, and a failed read shows "—" per row rather than
  // blocking the page (I14).
  const firstBatchLanded = $derived(documents.rows.length > 0 || !documents.isLoading);
  const isComplete = $derived(!documents.isLoading);
  const isLoading = $derived(!firstBatchLanded || metadataState.isLoading);
  const statusState = $derived($orgExposedSensitiveDocumentsStatusStore.state);

  // `totalCount` is null until the count query lands, which may be after the first batch.
  const spinnerMessage = $derived(
    documents.totalCount === null
      ? "Loading exposed sensitive documents..."
      : `Loading ${formatNumberWithCommas(documents.loadedCount)} of ${formatNumberWithCommas(documents.totalCount)} documents`,
  );
  // Which of the spinner's three reads have answered: the count, the first batch, the metadata.
  // The spinner is keyed on it, so its 30 s "Oops" timer restarts each time one answers and fires
  // only when 30 s pass with none answering. Later batches are not in it: while the metadata alone
  // holds the spinner, batches still landing must not keep a hung metadata read from timing out.
  const spinnerProgress = $derived([documents.totalCount !== null, firstBatchLanded, !metadataState.isLoading].join());
  const loadingProgress = $derived(
    documents.totalCount ? Math.min(100, (documents.loadedCount / documents.totalCount) * 100) : 0,
  );

  const metadata = $derived(metadataState.metadata);
  const skippedProviders = $derived(providersWithStatus(metadata, "skipped"));

  const liveRows = $derived(dropRetiredRows(documents.rows, metadata));
  const providerRows = $derived(liveRows.filter((row) => rowMatchesProvider(row, providerFilter)));

  // An unresolved org default is show-all, so an in-flight GET renders the whole page rather than
  // flashing an error or an empty table, and the saved list simply applies when it lands. It is
  // not *settled* show-all, though, so the filter bar holds its controls back until it is.
  const activeInfoTypes = $derived(resolveActiveInfoTypes(filters.infoTypes, orgDefaultInfoTypes));

  /**
   * The active raw names as a set, or `null` under show-all. The row detail panel (#2205) takes
   * it as its `activeInfoTypes` prop, so the panel's info-type groups show exactly what the table
   * row showed: one active list, one number, wherever the reviewer looks.
   */
  const activeInfoTypeSet = $derived(activeInfoTypes.infoTypes === null ? null : new Set(activeInfoTypes.infoTypes));

  const trimmedRows = $derived(trimRowsToInfoTypes(providerRows, activeInfoTypes.infoTypes));
  const statusCounts = $derived(countByStatus(trimmedRows.map((trimmed) => trimmed.row)));
  // Like the status counts, each filter's counts ignore its own pick: every provider is counted
  // under the info-type and status filters, not the provider filter.
  const providerCounts = $derived.by(() => {
    const rows = filterRowsByStatus(
      trimRowsToInfoTypes(liveRows, activeInfoTypes.infoTypes),
      filters.statuses,
      statusState,
    );
    return PROVIDERS.map((provider) => ({
      provider,
      count: rows.filter(({ row }) => rowMatchesProvider(row, provider)).length,
    }));
  });
  const statusRows = $derived(filterRowsByStatus(trimmedRows, filters.statuses, statusState));
  const counts = $derived(countByExposureKind(statusRows.map((trimmed) => trimmed.row)));
  const exposureRows = $derived(statusRows.filter(({ row }) => rowMatchesExposure(row, exposureFilter)));
  // A failed status read leaves nothing to rank on, so a Status sort falls back to the default.
  const appliedSort = $derived(effectiveSort(sort, statusState));
  const sortedRows = $derived(sortTrimmedRows(exposureRows, appliedSort));
  const page = $derived(paginate(sortedRows, currentPage, DEFAULT_PAGE_SIZE));

  // --- The category filter bar ----------------------------------------------------------------

  const filterControls = $derived(infoTypeFilterControls(activeInfoTypes, orgDefaultInfoTypes));

  // "Show all" writes the empty `?infoTypes=`; it is never the org default (spec 6.3, #2244).
  const onShowAllInfoTypes = () => setFilters({ infoTypes: SHOW_ALL_INFO_TYPES });

  const onBackToOrgDefault = () => setFilters({ infoTypes: FOLLOW_ORG_DEFAULT });

  // Only a trimmed list renders chips, so there is always an active list to remove from: under
  // show-all `infoTypeChips` is built from `[]` and the bar has no × to press.
  const onRemoveInfoTypes = (rawNames: string[]) => {
    setFilters({ infoTypes: removeInfoTypes(activeInfoTypes.infoTypes ?? [], rawNames) });
  };

  // --- The category search (#2247) ----------------------------------------------------------

  /**
   * The rows the category search offers categories from: the current **exposure + provider** view
   * (spec 6.3), deliberately *not* trimmed to the active list. Trimmed rows would only ever offer
   * the categories already ticked, so nothing could be added back.
   */
  const viewRows = $derived(providerRows.filter((row) => rowMatchesExposure(row, exposureFilter)));

  /**
   * The line-3 chips. The "k of n types" denominator is the category search's own membership over
   * the same rows, so a chip is partial exactly when the search shows that category's tick as
   * indeterminate (#2249) - one rule in both places.
   */
  const categoryMembership = $derived(
    categoryMembershipOf(viewRows, {
      active: activeInfoTypes.infoTypes,
      customInfoTypes: enabledCustomInfoTypes,
    }),
  );
  const infoTypeChips = $derived(
    activeCategoryChips(activeInfoTypes.infoTypes ?? [], {
      candidateInfoTypes: categoryMembership,
      customInfoTypes: enabledCustomInfoTypes,
    }),
  );

  /**
   * Only Admins (`org:write`) and Redactive staff may change what the whole org sees; everyone else
   * still gets to pick for themselves. The management PUT applies the same rule.
   */
  let isRedactiveStaff = $state(false);
  $effect(() => {
    const email = $authStateStore.userInfo?.email;
    isRedactiveStaff = false;
    if (!email) return;
    isRedactiveUser(email)
      .then((staff) => {
        if ($authStateStore.userInfo?.email === email) isRedactiveStaff = staff;
      })
      .catch(() => {});
  });
  const canWriteOrgDefault = $derived(
    hasOrgClaim($selectedOrgStore, $authStateStore.userInfo?.email, "org:write") || isRedactiveStaff,
  );

  /**
   * Write the org default, then adopt what came back: `saveDefaultInfoTypes` returns the route's
   * **echoed** list (de-duplicated and ordered server-side, R4), and `afterOrgDefaultSaved` turns
   * it into the two things the page changes - the adopted default and the URL selection. No second
   * GET to watch land. A failed PUT says so and leaves the active list as it was.
   *
   * With no org selected there is no settings document to write, so this returns quietly and says
   * nothing: the controls are only rendered under an org, so the branch is unreachable from the
   * UI and an error toast would be about the page's own state, not about anything the reader did.
   *
   * The `savingFor` capture is the same stale guard the settings and custom-info-type reads use:
   * an org switch mid-PUT must not plant the old org's default on the new one - which would also
   * race the new org's GET - nor raise a toast about an org nobody is looking at any more.
   */
  const putOrgDefault = async (infoTypes: string[]) => {
    const savingFor = orgId;
    if (!savingFor) return;
    try {
      const saved = await saveDefaultInfoTypes(savingFor, infoTypes);
      if (savingFor !== orgId) return;
      const adopted = afterOrgDefaultSaved(saved);
      orgDefaultInfoTypes = adopted.orgDefault;
      setFilters({ infoTypes: adopted.filters });
      toaster.success("Org default saved", "Everyone in this organisation now sees these categories by default.");
    } catch (error) {
      if (savingFor !== orgId) return;
      toaster.error("Could not save the org default", error instanceof Error ? error.message : String(error));
    }
  };

  /**
   * Save writes the **active** list - what the filter bar is showing: the bar's button is the one
   * the spec puts there (483), and every tick in the category search is already active.
   *
   * Uncategorised is expanded at the moment of the save (spec 8.0 R5) and against **every loaded
   * row**, not just the provider on screen: the default is the org's, so a raw name that only a
   * Google Drive document carries must survive a save made while looking at SharePoint. Rows from
   * a retired provider are already out of `liveRows` and stay out. That is also why the save is
   * the one control that waits for the last batch (#2265): over a partial load it would drop the
   * raw names only the unloaded rows carry.
   */
  const onSaveAsOrgDefault = () =>
    void putOrgDefault(
      expandForSave(activeInfoTypes.infoTypes ?? [], liveRows, { customInfoTypes: enabledCustomInfoTypes }),
    );

  const shownMetadata = $derived(metadataForProvider(metadata, providerFilter));
  const skippedOnScreen = $derived(
    skippedMetadataOnScreen(
      shownMetadata,
      page.items.map((trimmed) => trimmed.row),
    ),
  );

  // The panel shows a row from the page on screen, never a remembered copy: if a re-query, a page
  // change or a filter drops the selected row out of `page.items`, the panel closes rather than
  // describing a document the reviewer can no longer see. Selection therefore does not survive a
  // page change - one `find` over 25 rows, so there is nothing here worth a view-model function.
  const selectedRow = $derived(page.items.find(({ row }) => row.id === selectedRowId)?.row ?? null);

  // D2 of the reopened line: the publish time of the selected row's own provider, and only while
  // that provider's metadata reads `published` (already on the client, so no read on panel open).
  const selectedPublishedAt = $derived(publishedAtOf(metadata, selectedRow?.provider));

  // Save one row's status (#2253), then patch the status store in place: no reload. A patch that
  // moves the row out of the status filter or the Status sort's page closes the panel through
  // `selectedRow` above. `swept` and `failed` leave the store alone, so the row is never dropped
  // client-side. Ids are captured before the await: the selection may move during the save.
  const saveSelectedStatus: SaveStatus = async (status, note) => {
    const savingOrg = orgId;
    const documentId = selectedRow?.id;
    if (!savingOrg || !documentId) return { kind: "failed" };
    const outcome = await saveExposedDocumentStatus(savingOrg, documentId, status, note);
    // The store drops the patch if the org switched during the save.
    if (outcome.kind === "saved") {
      orgExposedSensitiveDocumentsStatusStore.setStatus(savingOrg, documentId, outcome.record);
    }
    return outcome;
  };

  const emptyVariant = $derived(
    emptyStateVariant({ metadata, liveRowCount: liveRows.length, filteredRowCount: sortedRows.length }),
  );

  const onSort = (column: SortColumn, direction: SortDirection) => {
    sort = { column, direction };
  };

  const onPageChange = (direction: "next" | "prev") => {
    currentPage = Math.min(Math.max(currentPage + (direction === "next" ? 1 : -1), 1), Math.max(page.pageCount, 1));
  };
</script>

{#if isLoading}
  <!-- Keyed on the reads that have answered, not the message: see `spinnerProgress`. -->
  {#key spinnerProgress}
    <PageLoadingSpinner timeout={30_000} message={spinnerMessage} />
  {/key}
{:else}
  <div class="flex flex-col gap-3">
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {#each EXPOSURE_FILTERS as kind (kind)}
        <ExposureCounterCard
          {kind}
          count={counts[kind]}
          provisional={!isComplete}
          active={exposureFilter === kind}
          onSelect={(next) => setFilters({ exposure: next })}
        />
      {/each}
    </div>

    <!-- The Permissions Assurance page's progress bar. Unlike that page, nothing waits on it but
         the org-default save: sort and filters work over the rows loaded so far. -->
    {#if !isComplete}
      <div
        class="w-full bg-gray-200 rounded-full h-1"
        role="progressbar"
        aria-label="Loading exposed sensitive documents"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(loadingProgress)}
        data-testid="exposed-documents-progress"
      >
        <div class="bg-blue-600 h-1 rounded-full transition-all duration-300" style="width: {loadingProgress}%"></div>
      </div>
    {/if}

    <div class="rounded-lg bg-white shadow px-3 py-2 flex flex-col gap-y-1.5 text-xs">
      <div class="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <ExposureProviderSelect
          value={providerFilter}
          counts={providerCounts}
          provisional={!isComplete}
          onChange={(provider) => setFilters({ provider })}
        />
        <ExposureStatusSelect
          selected={filters.statuses}
          counts={statusCounts}
          provisional={!isComplete}
          disabled={statusState === "failed"}
          onChange={(statuses) => setFilters({ statuses })}
        />
      </div>
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <CategorySearchCombobox
          rows={viewRows}
          active={activeInfoTypes.infoTypes}
          customInfoTypes={enabledCustomInfoTypes}
          provisional={!isComplete}
          onChange={(selection) => setFilters({ infoTypes: selection })}
        />
        <CategoryFilterBar
          controls={filterControls}
          canWrite={canWriteOrgDefault}
          saveDisabled={!isComplete}
          onShowAll={onShowAllInfoTypes}
          {onBackToOrgDefault}
          {onSaveAsOrgDefault}
        />
      </div>
      <ActiveCategoryChips chips={infoTypeChips} onRemove={onRemoveInfoTypes} />
    </div>

    <ExposureTruncationNotice metadata={shownMetadata} />
    <SkippedProviderWarning metadata={skippedOnScreen} />

    {#if documents.error}
      <div class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
        Could not load the exposed sensitive documents: {documents.error}
      </div>
    {/if}

    {#if emptyVariant}
      <ExposedDocumentsEmptyState
        variant={emptyVariant}
        {exposureFilter}
        {providerFilter}
        totalRows={liveRows.length}
        provisional={!isComplete}
        infoTypesFiltered={activeInfoTypes.infoTypes !== null}
        statusFilter={statusState === "failed" ? [] : filters.statuses}
        onShowAll={() => setFilters(NO_EXPOSURE_FILTERS)}
      />
    {:else}
      <!-- The panel sits beside the table and narrows it (spec 6.4): the table flexes, the panel
           keeps its own fixed width. -->
      <div class="flex items-start gap-3">
        <div class="min-w-0 flex-1">
          <ExposedDocumentsTable
            {page}
            provisional={!isComplete}
            sort={appliedSort}
            {onSort}
            {onPageChange}
            {skippedProviders}
            {statusState}
            customInfoTypes={enabledCustomInfoTypes}
            {selectedRowId}
            onSelect={(row) => (selectedRowId = row.id)}
          />
        </div>
        {#if selectedRow && orgId}
          <!-- The panel is trimmed to the same active info-type list as the row behind it (#2206);
               `null` there is show-all, which is the whole count map. -->
          <ExposedDocumentDetailPanel
            row={selectedRow}
            activeInfoTypes={activeInfoTypeSet}
            customInfoTypes={enabledCustomInfoTypes}
            {statusState}
            publishedAt={selectedPublishedAt}
            onSaveStatus={saveSelectedStatus}
            onClose={() => (selectedRowId = null)}
          />
        {/if}
      </div>
    {/if}
  </div>
{/if}
